import type { CSSProperties, ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { sidebarPanelSpring } from '@/shared/constants/motion.constants';
import { PANEL_RESIZE_TRACK_WIDTH, PanelResizeHandle } from '../PanelResize';

export interface DetailSidebarView {
  value: string;
  /** Rendered copy — callers interpolate any count into it themselves. */
  label: string;
  /** Owns its own padding; the shell supplies the card and the scroll box. */
  content: ReactNode;
}

export interface DetailSidebarProps {
  views: DetailSidebarView[];
  /** The open view. Anything that matches no view collapses the rail. */
  view: string | undefined;
  width: number;
  onResize: (next: number) => void;
  onResizeEnd: (next: number) => void;
  resizeLabel: string;
  dataTestId?: string;
  resizeHandleTestId?: string;
}

/**
 * The collapsible, resizable right rail shared by the asset and brief detail
 * surfaces: a resize handle plus a rounded card headed by the open view's
 * label. One view shows at a time, chosen by the host's toggles.
 *
 * Width is persisted per surface by `usePanelWidth`; the open view is not,
 * matching the brief page it was lifted from. The host owns both pieces of
 * state (see `useDetailSidebar`) because the toggles live in a page header,
 * far from the rail itself.
 */
export function DetailSidebar(props: DetailSidebarProps) {
  const shouldReduceMotion = useReducedMotion();

  const activeView = props.views.find((view) => view.value === props.view);
  const railWidth = props.width + PANEL_RESIZE_TRACK_WIDTH;

  const rail = activeView && (
    <>
      <PanelResizeHandle
        side="right"
        width={props.width}
        onResize={props.onResize}
        onResizeEnd={props.onResizeEnd}
        label={props.resizeLabel}
        dataTestId={props.resizeHandleTestId}
      />
      <div
        data-testid={props.dataTestId}
        style={{ width: props.width }}
        className="flex min-h-0 shrink-0 flex-col"
      >
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg bg-card">
          <div
            data-testid="detail-sidebar-header"
            className="shrink-0 border-b border-border-light p-md"
          >
            <h2 className="text-base font-medium">{activeView.label}</h2>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{activeView.content}</div>
        </div>
      </div>
    </>
  );

  if (shouldReduceMotion) {
    if (!activeView) return null;
    return (
      <div style={{ width: railWidth }} className="flex min-h-0 shrink-0 overflow-hidden">
        {rail}
      </div>
    );
  }

  // One constant key across every view, so switching view swaps the body in
  // place. Only opening and closing crosses AnimatePresence.
  return (
    <AnimatePresence initial={false}>
      {activeView && (
        <motion.div
          key="detail-sidebar-rail"
          data-testid="detail-sidebar-motion"
          style={
            {
              '--detail-sidebar-rail-width': `${String(railWidth)}px`,
              width: 'calc(var(--detail-sidebar-rail-width) * var(--detail-sidebar-reveal))',
            } as CSSProperties
          }
          initial={{ '--detail-sidebar-reveal': 0, opacity: 0 }}
          animate={{ '--detail-sidebar-reveal': 1, opacity: 1 }}
          exit={{ '--detail-sidebar-reveal': 0, opacity: 0 }}
          transition={sidebarPanelSpring}
          className="flex min-h-0 shrink-0 overflow-hidden"
        >
          {rail}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
